import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Globe, Clock, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = '';

export default function TutorsPage() {
  const { user, logout } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/tutor-profiles`);
      setProfiles(data);
    } catch (error) {
      console.error('Error fetching tutor profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F3F4F6]">
      <nav className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_elite-tutoring-1/artifacts/0zn1n7em_1000075255.png" 
                alt="ESL Elite Logo" 
                className="h-12 w-auto"
              />
            </Link>
            <div className="flex items-center gap-6">
              <Link to="/" className="text-sm font-semibold hover:text-[#002FA7]">Home</Link>
              <Link to="/about" className="text-sm font-semibold hover:text-[#002FA7]">About Us</Link>
              <Link to="/tutors" className="text-sm font-semibold text-[#002FA7]">Our Tutors</Link>
              {user ? (
                <>
                  <Link
                    to={user.role === 'admin' ? '/admin' : user.role === 'tutor' ? '/tutor' : '/student'}
                    className="text-sm font-semibold hover:text-[#002FA7]"
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={logout}
                    className="text-sm font-semibold px-4 py-2 rounded-md border border-slate-900 hover:bg-slate-900 hover:text-white"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-sm font-semibold hover:text-[#002FA7]">Login</Link>
                  <Link
                    to="/register"
                    className="text-sm font-semibold px-4 py-2 rounded-md bg-[#002FA7] text-white hover:bg-[#001a5c]"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl font-black tracking-tighter mb-4" data-testid="tutors-page-heading">
              Our Expert Tutors
            </h1>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
              Meet the dedicated professionals who make learning a rewarding experience
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12 text-slate-600">Loading tutors...</div>
          ) : profiles.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600 text-lg mb-4">Tutor profiles coming soon!</p>
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#002FA7] text-white font-bold rounded-md hover:bg-[#001a5c]"
              >
                Register as a Student <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {profiles.map((profile) => (
                <div
                  key={profile.profile_id}
                  className="bg-white border border-slate-200 overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                  data-testid={`tutor-card-${profile.profile_id}`}
                >
                  <div className="h-72 bg-slate-100 overflow-hidden">
                    {profile.photo_url ? (
                      <img
                        src={profile.photo_url}
                        alt={`${profile.name} - Professional tutor`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#002FA7]/10 to-[#002FA7]/5">
                        <span className="text-6xl font-black text-[#002FA7]/20">
                          {profile.name?.charAt(0) || 'T'}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-2">{profile.name}</h3>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {profile.subjects?.map((subject, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-[#002FA7]/10 text-[#002FA7] text-xs font-semibold rounded"
                        >
                          <BookOpen className="w-3 h-3" /> {subject}
                        </span>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {profile.languages?.map((lang, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-[#E32636]/10 text-[#E32636] text-xs font-semibold rounded"
                        >
                          <Globe className="w-3 h-3" /> {lang}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-1 text-sm text-slate-500 mb-3">
                      <Clock className="w-4 h-4" />
                      <span>{profile.years_experience}+ years experience</span>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed">{profile.bio}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="text-center mt-16">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#002FA7] text-white font-bold rounded-md hover:bg-[#001a5c] text-lg"
              data-testid="tutors-cta-button"
            >
              Book a Session Today <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_elite-tutoring-1/artifacts/0zn1n7em_1000075255.png" 
                alt="ESL Elite Logo" 
                className="h-10 w-auto"
              />
            </div>
            <div className="text-sm text-slate-400">
              © 2026 Elite French-English & Math Tutoring. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
